import { PropsWithChildren, useState } from 'react';

import { useQuery, gql } from '@apollo/client';
import { Menu, QueryStats } from '@mui/icons-material';
import {
  Close,
  ChevronLeft,
  SpaceDashboard,
  List as ListIcon,
  SupportAgent,
  OpenInNew,
  Place,
  Help,
  Edit,
  Scoreboard,
} from '@mui/icons-material';
import {
  Stack,
  Box,
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer as MuiDrawer,
  AppBarProps as MuiAppBarProps,
  styled,
  Theme,
  CSSObject,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { ModalLink } from '@scorenco/components';
import { RouteLink, Types, useTranslation } from '@scorenco/core';
import Head from 'next/head';
import NextLink from 'next/link';
import { useRouter } from 'next/router';

import {
  AdminDjango,
  ButtonDocumentation,
  EditChallengeModal,
} from '@/components';
import { useCurrentClubContext } from '@/layouts';
import { T_KEYS } from '@/translations';
import { pickTextColorBasedOnBgColorSimple } from '@/utils';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

export type TournamentsLayoutComponentProps = PropsWithChildren<{
  challengeMenu?: Array<{ label: string; href: string; icon: JSX.Element }>;
  challenge?: Types.Tournaments_Challenge;
}>;

export const TournamentsLayoutComponent = ({
  children,
  challengeMenu,
  challenge,
}: TournamentsLayoutComponentProps) => {
  const { currentClub } = useCurrentClubContext();
  const router = useRouter();
  const { t } = useTranslation();
  const [open, setOpen] = useState(
    localStorage.getItem('drawerTournamentOpen') !== 'false'
  );

  const getTournamentName = useQuery(
    gql`
      query GetTournamentName($tournamentId: Int!) {
        tournaments_tournament_by_pk(id: $tournamentId) {
          id
          name
        }
      }
    `,
    {
      variables: {
        tournamentId: Number(router.query.tournamentId),
      },
      skip: !router.query.tournamentId,
      context: {
        role: 'club',
      },
    }
  );

  const handleDrawerOpen = () => {
    setOpen(true);
    localStorage.setItem('drawerTournamentOpen', 'true');
  };

  const handleDrawerClose = () => {
    setOpen(false);
    localStorage.setItem('drawerTournamentOpen', 'false');
  };

  return (
    <>
      <Head>
        <title>
          {(challenge?.name ? challenge?.name + ' - ' : '') +
            (getTournamentName?.data?.tournaments_tournament_by_pk?.name ||
              '') +
            ' - ' +
            t(T_KEYS.TOURNAMENTS_TITLE)}
          {' / ' + currentClub?.name + " | Score'n'co Administration"}
        </title>
      </Head>
      {!challenge?.name && (
        <AdminDjango
          service="tournaments"
          table="tournament"
          id={router.query.tournamentId}
        />
      )}
      <Box sx={{ display: 'flex' }}>
        <AppBar
          position="fixed"
          open={open}
          color="secondary"
          sx={{
            backgroundColor: challenge?.color,
            color: pickTextColorBasedOnBgColorSimple(
              challenge?.color || '#000000',
              '#fff',
              '#000'
            ),
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                marginRight: 5,
                ...(open && { display: 'none' }),
              }}
            >
              <Menu />
            </IconButton>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                flexGrow: 1,
                mr: 2,
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
              }}
              noWrap
            >
              {!!challenge?.name && (
                <ModalLink
                  Modal={EditChallengeModal}
                  modalProps={{ challenge }}
                >
                  <Stack
                    flexDirection="row"
                    alignItems="center"
                    sx={{ cursor: 'pointer' }}
                  >
                    {challenge?.name}
                    <IconButton
                      sx={{ color: 'white', pb: 1, mr: 1, ml: 0.5 }}
                      size="small"
                    >
                      <Edit
                        sx={{
                          fontSize: '1.2rem',
                          color: pickTextColorBasedOnBgColorSimple(
                            challenge?.color || '#000000',
                            '#fff',
                            '#000'
                          ),
                        }}
                      />
                    </IconButton>
                  </Stack>
                </ModalLink>
              )}
              {!!challenge?.name && ' - '}
              {getTournamentName?.data?.tournaments_tournament_by_pk?.name}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <NextLink
                legacyBehavior
                href={`/${currentClub.id}/tournaments`}
                passHref
              >
                <IconButton edge="end" color="inherit">
                  <Close />
                </IconButton>
              </NextLink>
            </Stack>
          </Toolbar>
        </AppBar>
        <Drawer variant="permanent" open={open}>
          <DrawerHeader>
            <IconButton onClick={handleDrawerClose}>
              <ChevronLeft />
            </IconButton>
          </DrawerHeader>
          <Divider />
          <List>
            <RouteLink
              href={`/${currentClub.id}/tournaments/${router.query.tournamentId}`}
            >
              <ListItem disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  sx={{
                    minHeight: '3rem',
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                    backgroundColor:
                      router.asPath ===
                      `/${currentClub.id}/tournaments/${router.query.tournamentId}`
                        ? 'primary.lighter'
                        : 'transparent',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    <SpaceDashboard />
                  </ListItemIcon>
                  <ListItemText
                    primary="Vue d'ensemble"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>
            </RouteLink>
            <RouteLink
              href={`/${currentClub.id}/tournaments/${router.query.tournamentId}/challenges`}
            >
              <ListItem disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  sx={{
                    minHeight: '3rem',
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                    backgroundColor:
                      router.asPath ===
                      `/${currentClub.id}/tournaments/${router.query.tournamentId}/challenges`
                        ? 'primary.lighter'
                        : 'transparent',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    <ListIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Challenges"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>
            </RouteLink>
            <RouteLink
              href={`/${currentClub.id}/tournaments/${router.query.tournamentId}/reporters`}
            >
              <ListItem disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  sx={{
                    minHeight: '3rem',
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                    backgroundColor:
                      router.asPath ===
                      `/${currentClub.id}/tournaments/${router.query.tournamentId}/reporters`
                        ? 'primary.lighter'
                        : 'transparent',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    <SupportAgent />
                  </ListItemIcon>
                  <ListItemText
                    primary="Reporters officiels"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>
            </RouteLink>
            <RouteLink
              href={`/${currentClub.id}/tournaments/${router.query.tournamentId}/places`}
            >
              <ListItem
                disablePadding
                sx={{ display: 'block' }}
                component="a"
                target="_blank"
              >
                <ListItemButton
                  sx={{
                    minHeight: '3rem',
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                    backgroundColor:
                      router.asPath ===
                      `/${currentClub.id}/tournaments/${router.query.tournamentId}/places`
                        ? 'primary.lighter'
                        : 'transparent',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    <Place />
                  </ListItemIcon>
                  <ListItemText
                    primary="Terrains"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>
            </RouteLink>
            <RouteLink
              href={`/${currentClub.id}/tournaments/${router.query.tournamentId}/statistics`}
            >
              <ListItem
                disablePadding
                sx={{ display: 'block' }}
                component="a"
                target="_blank"
              >
                <ListItemButton
                  sx={{
                    minHeight: '3rem',
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                    backgroundColor:
                      router.asPath ===
                      `/${currentClub.id}/tournaments/${router.query.tournamentId}/statistics`
                        ? 'primary.lighter'
                        : 'transparent',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    <QueryStats />
                  </ListItemIcon>
                  <ListItemText
                    primary="Statistiques"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>
            </RouteLink>
            <RouteLink
              href={`${process.env.NEXT_PUBLIC_TOURNAMENT_URL}/${router.query.tournamentId}`}
            >
              <ListItem
                disablePadding
                sx={{ display: 'block' }}
                component="a"
                target="_blank"
              >
                <ListItemButton
                  sx={{
                    minHeight: '3rem',
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    <OpenInNew />
                  </ListItemIcon>
                  <ListItemText
                    primary="Ouvrir le tournoi"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>
            </RouteLink>
            <RouteLink
              href={`/${currentClub.id}/tournaments/${router.query.tournamentId}/scores`}
            >
              <ListItem
                disablePadding
                sx={{ display: 'block' }}
                component="a"
                target="_blank"
              >
                <ListItemButton
                  sx={{
                    minHeight: '3rem',
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                    backgroundColor:
                      router.asPath ===
                      `/${currentClub.id}/tournaments/${router.query.tournamentId}/scores`
                        ? 'primary.lighter'
                        : 'transparent',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    <Scoreboard />
                  </ListItemIcon>
                  <ListItemText
                    primary="Rentrer les scores"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>
            </RouteLink>
            <RouteLink href="https://aide.scorenco.com/fr/article/questions-frequentes-logiciel-tournois-p7l4kj/">
              <ListItem
                disablePadding
                sx={{ display: 'block' }}
                component="a"
                target="_blank"
              >
                <ListItemButton
                  sx={{
                    minHeight: '3rem',
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    <Help />
                  </ListItemIcon>
                  <ListItemText
                    primary="Questions fréquentes"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>
            </RouteLink>
          </List>
          {!!challengeMenu && (
            <>
              <Divider />
              <List>
                {challengeMenu?.map((menu) => (
                  <RouteLink href={menu.href} key={menu.label}>
                    <ListItem disablePadding sx={{ display: 'block' }}>
                      <ListItemButton
                        sx={{
                          minHeight: '3rem',
                          justifyContent: open ? 'initial' : 'center',
                          px: 2.5,
                          backgroundColor:
                            router.asPath === menu.href
                              ? 'primary.lighter'
                              : 'transparent',
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 3 : 'auto',
                            justifyContent: 'center',
                          }}
                        >
                          {menu.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={menu.label}
                          sx={{ opacity: open ? 1 : 0 }}
                        />
                      </ListItemButton>
                    </ListItem>
                  </RouteLink>
                ))}
              </List>
            </>
          )}
        </Drawer>
        <Box
          component="main"
          sx={{ flexGrow: 1, p: 3, maxWidth: '100%', overflow: 'scroll' }}
        >
          <DrawerHeader />
          {children}
        </Box>
      </Box>
      <Box sx={{ marginLeft: '14rem' }}>
        <ButtonDocumentation link="https://aide.scorenco.com/fr/category/module-tournoi-4r17qi/" />
      </Box>
    </>
  );
};
